import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Lock, ArrowLeft, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [appName, setAppName] = useState('Regulisto');

  useEffect(() => {
    verifyToken();
    fetchAppName();
  }, [token]);

  const fetchAppName = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/public`);
      if (response.data?.app_name) {
        setAppName(response.data.app_name);
      }
    } catch (error) {
      console.error('Error fetching public settings:', error);
    }
  };

  const verifyToken = async () => {
    if (!token) {
      setTokenValid(false);
      setVerifying(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
      setTokenValid(response.data.valid);
      setMaskedEmail(response.data.email || '');
    } catch (err) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError(language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'es' ? 'Error al restablecer la contraseña' : 'Error resetting password'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600">
            {language === 'es' ? 'Verificando enlace...' : 'Verifying link...'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-['Manrope'] text-slate-900">{appName}</span>
          </div>
          
          {!tokenValid ? (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-xl">
                {language === 'es' ? 'Enlace inválido' : 'Invalid link'}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Este enlace ha expirado o no es válido. Por favor, solicita un nuevo enlace de recuperación.'
                  : 'This link has expired or is invalid. Please request a new recovery link.'}
              </CardDescription>
            </>
          ) : success ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-xl">
                {language === 'es' ? '¡Contraseña actualizada!' : 'Password updated!'}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Tu contraseña ha sido restablecida correctamente. Ya puedes iniciar sesión.'
                  : 'Your password has been reset successfully. You can now log in.'}
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-xl">
                {language === 'es' ? 'Nueva contraseña' : 'New password'}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? `Introduce tu nueva contraseña para la cuenta ${maskedEmail}`
                  : `Enter your new password for account ${maskedEmail}`}
              </CardDescription>
            </>
          )}
        </CardHeader>
        
        <CardContent>
          {!tokenValid ? (
            <div className="space-y-4">
              <Link to="/forgot-password">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                  {language === 'es' ? 'Solicitar nuevo enlace' : 'Request new link'}
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="w-full gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {language === 'es' ? 'Volver al inicio de sesión' : 'Back to login'}
                </Button>
              </Link>
            </div>
          ) : success ? (
            <Link to="/login">
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                {language === 'es' ? 'Iniciar sesión' : 'Log in'}
              </Button>
            </Link>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password">
                  {language === 'es' ? 'Nueva contraseña' : 'New password'}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  {language === 'es' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  {language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="reset-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {language === 'es' ? 'Actualizando...' : 'Updating...'}
                  </span>
                ) : (
                  language === 'es' ? 'Restablecer contraseña' : 'Reset password'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
